import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, GraduationCap, Save, Trash2, Copy, Edit3, Users, Bell, Send, Loader2, Search } from "lucide-react";
import GooglePlacesInput from "@/components/GooglePlacesInput";
import DuplicateModal from "@/components/admin/DuplicateModal";
import { BulkActionsBar, BulkCheckbox } from "@/components/admin/BulkActionsBar";
import NotifyContactsModal from "@/components/admin/NotifyContactsModal";

const LANGS = [{ v: "fr", l: "🇫🇷 Français" }, { v: "en", l: "🇬🇧 English" }, { v: "es", l: "🇪🇸 Español" }, { v: "it", l: "🇮🇹 Italiano" }, { v: "pt", l: "🇵🇹 Português" }];
const EMPTY = { title: "", description: "", long_description: "", language: "fr", format: "physical", date: "", end_date: "", virtual_link: "", virtual_password: "", location_name: "", address: "", city: "", country: "France", google_place_id: "", audience: "public", tickets: [], capacity: 100, image: "", status: "published" };

export default function AdminFormations() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/formations").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.patch(`/admin/formations/${editing.id}`, form);
      else await api.post("/admin/formations", form);
      toast.success(editing ? "Formation mise à jour" : "Formation créée");
      setCreating(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openEdit = (f) => { setEditing(f); setForm({ ...EMPTY, ...f, date: (f.date || "").slice(0, 16), end_date: (f.end_date || "").slice(0, 16) }); setCreating(true); };
  const del = async (f) => { if (!window.confirm(`Supprimer la formation "${f.title}" ?`)) return; await api.delete(`/admin/formations/${f.id}`); toast.success("Supprimée"); load(); };

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => { const ns = new Set(selected); if (ns.has(id)) ns.delete(id); else ns.add(id); setSelected(ns); };
  const selectAllVisible = () => setSelected(new Set(items.map((f) => f.id)));
  const clearSel = () => setSelected(new Set());
  const bulkDelete = async () => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT ${selected.size} formation${selected.size > 1 ? "s" : ""} ?\nCette action est IRRÉVERSIBLE.`)) return;
    try {
      const r = await api.post("/admin/formations/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.deleted} formation${r.data.deleted > 1 ? "s" : ""} supprimée${r.data.deleted > 1 ? "s" : ""}`);
      clearSel();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };
  const [dupTarget, setDupTarget] = useState(null);
  const duplicate = (f) => setDupTarget(f);
  const doDuplicate = async ({ shift, count }) => {
    try {
      const r = await api.post(`/admin/formations/${dupTarget.id}/duplicate`, { shift, count });
      toast.success(`${r.data.count} formation${r.data.count > 1 ? "s" : ""} dupliquée${r.data.count > 1 ? "s" : ""}`);
      setDupTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  // Bulk notify contacts modal (Sprint 73 quater) — uses shared NotifyContactsModal
  const [notifyOpen, setNotifyOpen] = useState(false);

  const addTicket = () => setForm({ ...form, tickets: [...(form.tickets || []), { id: `t-${Date.now()}`, name: "", price: 0, currency: "eur", capacity: 50 }] });
  const updTicket = (i, k, v) => { const ts = [...form.tickets]; ts[i] = { ...ts[i], [k]: v }; setForm({ ...form, tickets: ts }); };
  const rmTicket = (i) => setForm({ ...form, tickets: form.tickets.filter((_, idx) => idx !== i) });

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><GraduationCap className="mr-2 inline h-7 w-7 text-wine-600" />Formations</h1>
          <p className="mt-1 text-neutral-600">Créez et gérez les formations La WineTech Academy.</p>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing(null); setCreating(true); }} className="inline-flex items-center gap-2 rounded-full bg-wine-600 px-4 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="formation-new"><Plus className="h-3.5 w-3.5" />Nouvelle formation</button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        {selected.size > 0 && (
          <div className="border-b border-neutral-100 bg-white p-3">
            <BulkActionsBar
              count={selected.size}
              total={items.length}
              onClear={clearSel}
              onSelectAll={selectAllVisible}
              onDelete={bulkDelete}
              label="formation"
              testId="admin-formations-bulk"
              extraActions={
                <button onClick={() => setNotifyOpen(true)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="formations-bulk-notify">
                  <Bell className="h-3.5 w-3.5" /> Notifier des contacts
                </button>
              }
            />
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs text-neutral-600">
            <tr>
              <th className="px-3 py-2 w-8">
                <BulkCheckbox
                  checked={items.length > 0 && selected.size === items.length}
                  onChange={(v) => v ? selectAllVisible() : clearSel()}
                  testId="formations-select-all"
                />
              </th>
              <th className="px-4 py-2 text-left">Titre</th><th className="px-4 py-2 text-left">Langue</th><th className="px-4 py-2 text-left">Format</th><th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-left">Audience</th><th className="px-4 py-2 text-left">Tickets</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.id} className={`border-t border-neutral-100 ${selected.has(f.id) ? "bg-rose-50/40" : ""}`} data-testid={`formation-row-${f.id}`}>
                <td className="px-3 py-2"><BulkCheckbox checked={selected.has(f.id)} onChange={() => toggleSel(f.id)} testId={`formation-select-${f.id}`} /></td>
                <td className="px-4 py-2 font-medium"><Link to={`/admin/formations/${f.id}`} className="hover:text-wine-700" data-testid={`formation-title-${f.id}`}>{f.title}</Link></td>
                <td className="px-4 py-2">{(f.language || "fr").toUpperCase()}</td>
                <td className="px-4 py-2 capitalize">{f.format}</td>
                <td className="px-4 py-2 text-xs">{f.date ? new Date(f.date).toLocaleString("fr") : "—"}</td>
                <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${f.audience === "members_only" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{f.audience === "members_only" ? "🔒 Membres" : "🌐 Public"}</span></td>
                <td className="px-4 py-2 text-xs">{(f.tickets || []).length === 0 ? "Gratuit" : `${f.tickets.length} ticket(s)`}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/admin/formations/${f.id}`} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-wine-50 hover:text-wine-700" title="Voir les inscrits" data-testid={`formation-detail-${f.id}`}><Users className="h-3.5 w-3.5" /></Link>
                    <button onClick={() => openEdit(f)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Modifier" data-testid={`formation-edit-${f.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => duplicate(f)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-blue-50 hover:text-blue-700" title="Dupliquer" data-testid={`formation-duplicate-${f.id}`}><Copy className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(f)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-700" title="Supprimer" data-testid={`formation-del-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-neutral-500">Aucune formation.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="formation-modal">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between"><h3 className="text-xl font-bold text-navy">{editing ? "Modifier la formation" : "Nouvelle formation"}</h3><button onClick={() => { setCreating(false); setEditing(null); }}><X className="h-5 w-5" /></button></div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre *" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" data-testid="formation-title" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description courte" rows={2} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" data-testid="formation-desc" />
              <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="formation-lang">
                {LANGS.map((l) => <option key={l.v} value={l.v}>{l.l}</option>)}
              </select>
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="formation-format">
                <option value="physical">📍 Physique</option>
                <option value="digital">💻 Digital</option>
                <option value="hybrid">🌍 Hybride</option>
              </select>
              <input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="formation-date" />
              <input type="datetime-local" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" placeholder="Fin (optionnel)" />
              <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" data-testid="formation-audience">
                <option value="public">🌐 Ouvert au public</option>
                <option value="members_only">🔒 Réservé aux membres</option>
              </select>
              {form.format !== "digital" && (
                <>
                  <input value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} placeholder="Nom du lieu" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" />
                  <div className="sm:col-span-2">
                    <GooglePlacesInput value={form.address} placeholder="Adresse — tapez pour rechercher…" testId="formation-address" onSelect={(p) => setForm({ ...form, address: p.address, city: p.city || form.city, country: p.country || form.country, google_place_id: p.place_id || "", location_name: form.location_name || p.location_name || "" })} />
                  </div>
                  <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
                  <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Pays" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
                </>
              )}
              {form.format !== "physical" && (
                <>
                  <input value={form.virtual_link} onChange={(e) => setForm({ ...form, virtual_link: e.target.value })} placeholder="Lien visio (Zoom / Meet / Teams)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" data-testid="formation-link" />
                  <input value={form.virtual_password} onChange={(e) => setForm({ ...form, virtual_password: e.target.value })} placeholder="Mot de passe visio (optionnel)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" />
                </>
              )}
              <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="URL image (optionnel)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm sm:col-span-2" />
            </div>

            <div className="mt-4 rounded-xl border border-neutral-100 bg-neutral-50/40 p-3">
              <div className="flex items-center justify-between"><div className="text-xs font-bold uppercase text-neutral-700">Tickets dynamiques (laisser vide = gratuit)</div><button onClick={addTicket} className="rounded-full bg-wine-600 px-3 py-1 text-[10px] font-bold text-white hover:bg-wine-700" data-testid="ticket-add"><Plus className="mr-1 inline h-3 w-3" />Ajouter</button></div>
              <div className="mt-2 space-y-2">
                {(form.tickets || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`ticket-row-${i}`}>
                    <input value={t.name} onChange={(e) => updTicket(i, "name", e.target.value)} placeholder="Nom (Early bird, Plein tarif…)" className="col-span-5 rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" />
                    <input type="number" value={t.price} onChange={(e) => updTicket(i, "price", parseFloat(e.target.value) || 0)} placeholder="Prix" className="col-span-2 rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" />
                    <input type="number" value={t.capacity} onChange={(e) => updTicket(i, "capacity", parseInt(e.target.value) || 0)} placeholder="Capacité" className="col-span-2 rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" />
                    <input value={t.currency || "eur"} onChange={(e) => updTicket(i, "currency", e.target.value)} className="col-span-2 rounded-lg border border-neutral-200 px-2 py-1.5 text-xs" />
                    <button onClick={() => rmTicket(i)} className="col-span-1 text-rose-500 hover:text-rose-700"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
                {(form.tickets || []).length === 0 && <div className="text-xs text-neutral-500">Formation gratuite — ajoutez un ticket pour la rendre payante.</div>}
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => { setCreating(false); setEditing(null); }} className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">Annuler</button>
              <button onClick={save} disabled={!form.title || !form.date} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-50" data-testid="formation-save"><Save className="h-3.5 w-3.5" />{editing ? "Mettre à jour" : "Créer"}</button>
            </div>
          </div>
        </div>
      )}

      {dupTarget && (
        <DuplicateModal
          title={dupTarget.title}
          sourceDate={dupTarget.date}
          kind="formation"
          onClose={() => setDupTarget(null)}
          onConfirm={doDuplicate}
        />
      )}

      {notifyOpen && (
        <NotifyContactsModal
          open={notifyOpen}
          onClose={() => { setNotifyOpen(false); clearSel(); }}
          entityType="formation"
          entityIds={Array.from(selected)}
          endpoint="/admin/formations/notify-contacts-bulk"
          payloadKey="formation_ids"
        />
      )}
    </PortalLayout>
  );
}
